import localDb, { STORE_BLOBS } from '@/common/localDb';
import { BLOB_STORAGE_RETRY_DELAY_MS, BLOB_STORAGE_URL, BLOB_CDN_URL } from '@/common/config';
import { logDEBUG } from '@/common/debug';
import { logError } from '@/common/helpers';

const RETRY_AFTER_MS = BLOB_STORAGE_RETRY_DELAY_MS;

export interface BlobCreate {
  text: string;
  expires_in_days?: number | null;
}

export interface BlobResponse {
  id: string;
  url: string;
  length: number;
  expires_utc: number;
}

type CachedBlobRecord = {
  id: string;
  text: string | null;
  status: number;
  expiresAtMs: number;
};

/**
 * Detect if a blob ID is likely a malformed market description (ASCII text encoded as hex)
 * Valid blob IDs are random 64-character hex strings from the blob CDN
 * Malformed descriptions contain readable ASCII text (created incorrectly by external scripts)
 */
function isBogusBlob(hexString: string): boolean {
  // If not 64 chars hex, it's definitely not a valid blob ID
  if (hexString.length !== 64 || !/^[0-9a-fA-F]+$/.test(hexString)) {
    return true;
  }

  // Try to decode as ASCII and check if it contains readable text
  let nullTerminatorFound = false;
  let printableCount = 0;

  for (let i = 0; i < hexString.length; i += 2) {
    const charCode = parseInt(hexString.slice(i, i + 2), 16);
    if (charCode === 0) {
      nullTerminatorFound = true;
      break; // Null terminator
    }
    // Count printable ASCII characters (space through ~)
    if (charCode >= 0x20 && charCode <= 0x7e) {
      printableCount++;
    }
  }

  // Valid blob IDs are random hex - they'll have some printable chars by chance
  // Bogus blobs are actual text strings like "BTC price prediction" encoded as hex
  // Key difference: Bogus blobs will be MOSTLY printable ASCII (>80% of bytes)
  // Random blob IDs will typically be <30% printable ASCII

  // Calculate ratio based on bytes up to null terminator (if found)
  const bytesChecked = nullTerminatorFound ? hexString.indexOf('00') / 2 : 32;
  const printableRatio = bytesChecked > 0 ? printableCount / bytesChecked : 0;

  // If >70% of bytes are printable ASCII AND there's a null terminator,
  // it's likely a C-style text string, not a random blob ID
  // Note: We check for null terminator within reasonable bounds (< 32 bytes)
  // to avoid false positives with actual blob IDs that happen to have high printable ratios
  if (printableRatio > 0.7 && nullTerminatorFound && bytesChecked < 32) {
    return true;
  }

  return false;
}

/**
 * Decode a bogus description from hex to ASCII text
 */
function decodeBogusBlob(hexString: string): string {
  let decodedText = '';
  for (let i = 0; i < hexString.length; i += 2) {
    const charCode = parseInt(hexString.slice(i, i + 2), 16);
    if (charCode === 0) break; // Null terminator
    decodedText += String.fromCharCode(charCode);
  }
  return decodedText.trim();
}

export async function createBlob(text: string, expiresInDays?: number | null): Promise<BlobResponse> {
  const data: BlobCreate = { text, expires_in_days: expiresInDays ?? null };
  const response = await fetch(BLOB_STORAGE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Blob creation failed with status ${response.status}`);
  }

  return response.json() as Promise<BlobResponse>;
}

async function getCachedBlob(id: string): Promise<CachedBlobRecord | null> {
  const db = await localDb.openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BLOBS, 'readonly');
    const store = tx.objectStore(STORE_BLOBS);
    const req = store.get(id);

    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function putCachedBlob(record: CachedBlobRecord): Promise<void> {
  const db = await localDb.openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BLOBS, 'readwrite');
    const store = tx.objectStore(STORE_BLOBS);
    const req = store.put(record);

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function readBlob(id: string): Promise<string> {
  // Check if this is a malformed market with text description instead of blob ID
  // Malformed descriptions are ASCII text encoded as hex (like "BTC price" = "4254432070726963650000...")
  // This happens when external scripts pass raw text instead of creating a blob first
  try {
    if (isBogusBlob(id)) {
      const decoded = decodeBogusBlob(id);
      return decoded || 'Malformed market (no detailed rules available)';
    }
  } catch {
    // If detection fails, continue with normal blob fetching
  }

  const now = Date.now();

  // 1️⃣ Check cache
  const cached = await getCachedBlob(id);

  if (cached && cached.expiresAtMs > now) {
    if (cached.status === 200 && cached.text !== null) {
      return cached.text;
    }

    // Previously failed but still inside retry window
    throw new Error(`Blob fetch previously failed with status ${cached.status}`);
  }

  // 2️⃣ Fetch from CDN
  const url = `${BLOB_CDN_URL}/${id}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      // Cache failed fetch with retry delay
      const record: CachedBlobRecord = {
        id,
        text: null,
        status: response.status,
        expiresAtMs: now + RETRY_AFTER_MS,
      };

      await putCachedBlob(record);

      throw new Error(`Blob fetch failed with status ${response.status}`);
    }

    const text = await response.text();

    // 200 OK → cache forever
    const record: CachedBlobRecord = {
      id,
      text,
      status: 200,
      expiresAtMs: Number.MAX_SAFE_INTEGER,
    };

    await putCachedBlob(record);

    return text;
  } catch (error: unknown) {
    // If error is already from our status check above, rethrow it
    if (error instanceof Error && error.message.includes('Blob fetch failed')) {
      throw error;
    }

    // Network error or other fetch failure
    const record: CachedBlobRecord = {
      id,
      text: null,
      status: 0, // 0 indicates network error
      expiresAtMs: now + RETRY_AFTER_MS,
    };

    await putCachedBlob(record);

    throw error;
  }
}

/**
 * Batch fetch multiple blobs in parallel.
 * Returns a Map of blob ID to text content.
 * Missing or failed blobs will have empty string values.
 */
export async function readBlobsBatch(ids: string[]): Promise<Map<string, string>> {
  const results = new Map<string, string>();

  // Filter out empty IDs and deduplicate
  const uniqueIds = Array.from(new Set(ids.filter((id) => id !== '')));

  if (uniqueIds.length === 0) {
    return results;
  }

  // Fetch all blobs in parallel
  const fetchPromises = uniqueIds.map(async (id) => {
    try {
      const text = await readBlob(id);
      return { id, text };
    } catch (error) {
      logError('neutraBlob', `fetching blob ${id}`, error);
      return { id, text: '' };
    }
  });

  const fetchedBlobs = await Promise.all(fetchPromises);

  // Build the result map
  for (const { id, text } of fetchedBlobs) {
    results.set(id, text);
  }

  return results;
}

/**
 * Pre-fetch common blobs to warm the cache on app initialization.
 * This is called during app startup to load frequently used blobs (like standard rules)
 * into IndexedDB cache, eliminating network latency for subsequent market loads.
 *
 * @param blobIds - Array of blob IDs to pre-fetch
 * @returns Promise that resolves when all blobs are fetched (or failed)
 */
export async function preFetchCommonBlobs(blobIds: string[]): Promise<void> {
  if (blobIds.length === 0) {
    return;
  }

  logDEBUG(`[BLOB PREFETCH] Pre-fetching ${blobIds.length} common blobs for cache warming...`);

  try {
    await readBlobsBatch(blobIds);
    logDEBUG(`[BLOB PREFETCH] Successfully pre-fetched ${blobIds.length} common blobs`);
  } catch (error) {
    logDEBUG('[BLOB PREFETCH] Error during blob pre-fetching:', error);
    // Don't throw - pre-fetching is a best-effort optimization
  }
}

export default {
  createBlob,
  readBlob,
  readBlobsBatch,
  preFetchCommonBlobs,
};
